"use client"

import { useState } from "react"
import {
    AlertTriangle,
    Check,
    ChevronDown,
    Clock,
    ExternalLink,
    Handshake,
    Laptop,
    Lock,
    type LucideIcon,
    MonitorPlay,
    RefreshCw,
    ShieldAlert,
    Target,
    TrendingUp,
    Users,
    Wrench,
} from "lucide-react"

import { Button } from "@/common/components/ui/button"
import { cn } from "@/common/lib/utils"
import { moodleCmid } from "@/modules/lernpfad/data/connectors"
import { buildRationale } from "@/modules/lernpfad/engine"
import type {
    BausteinFormat,
    CurriculumItem,
    CurriculumItemReason,
    EmployeeProfile,
    RationaleStrings,
} from "@/modules/lernpfad/engine"
import type { Localizer } from "@/modules/lernpfad/i18n/localize"
import { REASON_VAR } from "./charts/palette"

const REASON_ICON: Record<CurriculumItemReason, LucideIcon> = {
    pflicht: ShieldAlert,
    auffrischung: RefreshCw,
    voraussetzung: Lock,
    luecke: TrendingUp,
    ziel: Target,
}

const FORMAT_ICON: Record<BausteinFormat, LucideIcon> = {
    "e-learning": Laptop,
    praesenz: Users,
    webinar: MonitorPlay,
    selbststudium: Clock,
    "on-the-job": Wrench,
    social: Handshake,
}

interface CurriculumItemCardProps {
    item: CurriculumItem
    employee: EmployeeProfile
    asOf: string
    localizer: Localizer
    rationaleStrings: RationaleStrings
    onMarkDone?: (bausteinId: string) => void
    done?: boolean
    compact?: boolean
    /** Moodle connected: provider chip + cmid + in-place launch (flow 2). */
    moodleConnected?: boolean
    onLaunch?: (baustein: CurriculumItem["baustein"]) => void
    /** Completion arrived via the (simulated) LTI passback. */
    viaLti?: boolean
}

export default function CurriculumItemCard({
    item,
    employee,
    asOf,
    localizer,
    rationaleStrings,
    onMarkDone,
    done = false,
    compact = false,
    moodleConnected = false,
    onLaunch,
    viaLti = false,
}: CurriculumItemCardProps) {
    const [showRationale, setShowRationale] = useState(false)
    const { t } = localizer
    const Icon = REASON_ICON[item.reason]
    const FormatIcon = FORMAT_ICON[item.baustein.format]
    const color = REASON_VAR[item.reason]

    const content = localizer.baustein(item.baustein)

    // Computed, never fetched — a "why this?" that spins loses the room.
    const rationale =
        item.rationale ??
        buildRationale(item, employee, {
            ...rationaleStrings,
            role: localizer.role(employee.roleId, employee.roleLabel),
        })
    const overdue = item.dueBy != null && item.dueBy < asOf

    return (
        <div
            className={cn(
                "group relative overflow-hidden rounded-lg border bg-card lp-card-hover",
                overdue && "border-destructive/40",
                done && "opacity-45",
            )}
        >
            {/* Reason accent — the categorical hue, carried as a rail rather than
                a filled card so long lists stay calm. */}
            <span
                className="absolute inset-y-0 left-0 w-1"
                style={{ background: color }}
                aria-hidden
            />

            <div className={cn("pl-5", compact ? "p-3 pl-5" : "p-4 pl-6")}>
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                            <span
                                className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-semibold"
                                style={{ color, background: `color-mix(in srgb, ${color} 12%, transparent)` }}
                            >
                                <Icon className="size-3" />
                                {localizer.reason(item.reason)}
                            </span>
                            {overdue && (
                                <span className="inline-flex items-center gap-1 rounded-md bg-destructive px-1.5 py-0.5 text-xs font-semibold text-white">
                                    <AlertTriangle className="size-3" />
                                    {t("item.overdue")}
                                </span>
                            )}
                        </div>

                        <h4 className={cn("mt-2 font-medium leading-snug", done && "line-through")}>
                            {content.title}
                        </h4>
                        {!compact && (
                            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                                {content.description}
                            </p>
                        )}

                        <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                            <span className="inline-flex items-center gap-1 font-medium text-foreground">
                                <Clock className="size-3" />
                                {t("item.hours", { hours: item.baustein.durationHours })}
                            </span>
                            <span className="inline-flex items-center gap-1">
                                <FormatIcon className="size-3" />
                                {localizer.format(item.baustein.format)}
                            </span>
                            {moodleConnected && item.baustein.format === "e-learning" ? (
                                <span className="inline-flex items-center gap-1 rounded-md border border-primary/30 bg-primary/5 px-1.5 py-0.5 text-xs font-medium text-primary">
                                    Moodle · cmid {moodleCmid(item.baustein.id)}
                                </span>
                            ) : (
                                <span>{content.provider}</span>
                            )}
                            {item.dueBy && (
                                <span
                                    className={cn(
                                        "inline-flex items-center gap-1",
                                        overdue && "font-semibold text-destructive",
                                    )}
                                >
                                    <AlertTriangle className={cn("size-3", !overdue && "hidden")} />
                                    {t("item.due", { date: localizer.date(item.dueBy) })}
                                </span>
                            )}
                        </div>
                    </div>

                    {done && viaLti && (
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-md bg-emerald-500/10 px-1.5 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-400">
                            <Check className="size-3" />
                            {t("item.viaLti")}
                        </span>
                    )}
                    {onLaunch && !done && item.baustein.format === "e-learning" && (
                        <div className="flex shrink-0 flex-col items-end gap-1">
                            <Button size="sm" onClick={() => onLaunch(item.baustein)}>
                                <ExternalLink className="size-3.5" />
                                {t("player.launch")}
                            </Button>
                            <span className="text-xs text-muted-foreground">{t("player.via")}</span>
                        </div>
                    )}
                    {onMarkDone && !done && !(onLaunch && item.baustein.format === "e-learning") && (
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onMarkDone(item.baustein.id)}
                            className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                        >
                            <Check className="size-3.5" />
                            {t("item.done")}
                        </Button>
                    )}
                </div>

                <button
                    type="button"
                    onClick={() => setShowRationale((open) => !open)}
                    className="mt-2.5 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                >
                    <ChevronDown
                        className={cn("size-3.5 transition-transform", showRationale && "rotate-180")}
                    />
                    {t("item.why")}
                </button>

                {showRationale && (
                    <div
                        className="mt-2 rounded-md border-l-2 bg-muted/40 p-3 text-sm leading-relaxed"
                        style={{ borderColor: color }}
                    >
                        {rationale}
                    </div>
                )}
            </div>
        </div>
    )
}
